import cn from 'classnames';
import type { FC, ReactElement } from 'react';
import React, { Children, cloneElement, useCallback, useState, useEffect } from 'react';
import { changeHistory } from '@alfa-bank/site-helpers-common/browser/change-history';
import { useNamedContainersWithChildren } from '@alfa-bank/site-named-container/utils';

import { DEFAULT_BROWSER } from '../config';
import type { IChildTab } from '../tab/models';
import type { IOnChangeCallback } from '../../models';

import type { TTabs } from './models';
import styles from './styles/tabs.css';

export const Tabs: FC<React.PropsWithChildren<TTabs>> = ({
    TabsList,
    align = 'left',
    browser = DEFAULT_BROWSER,
    className = '',
    keepMounted = false,
    inline = true,
    mobile = false,
    onChange = () => {},
    selectedID,
    children,
    scroll = true,
    tabNamesStyler,
    tabNamesClassName,
    theme,
    forwardedRef,
    writeValueToQuery,
    ...tabListProps
}) => {
    const { background, children: otherChildren } = useNamedContainersWithChildren(children);
    const childrenArray = Children.toArray(otherChildren) as Array<ReactElement<IChildTab>>;
    const [tabID, setTabID] = useState(selectedID || childrenArray[0]?.props?.id);

    useEffect(() => {
        if (!selectedID || selectedID === tabID) return;
        setTabID(selectedID);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedID]);

    useEffect(() => {
        if (writeValueToQuery) {
            changeHistory(writeValueToQuery, tabID);
        }
    }, [tabID, writeValueToQuery]);

    /** Обрабатывает переключение Табы. */
    const handleOnChange = useCallback(
        ({ event, id }: IOnChangeCallback) => {
            setTabID(id);
            onChange({ event, id });
        },
        [onChange]
    );

    if (!childrenArray.length) return null;

    /** Генерирует заголовки Таб. */
    const headerTitles = childrenArray.map(({ props }) => ({
        id: props.id,
        title: props.title,
        iconSrc: props.iconSrc,
        dataLayer: props.dataLayer,
    }));

    let childrenFiltered = childrenArray;

    if (!keepMounted) {
        childrenFiltered = childrenArray.filter(
            ({ props }) => props.keepMounted || props.id === tabID
        );
    }

    const List = (
        <TabsList
            {...tabListProps}
            align={align}
            browser={mobile ? { ...browser, mobile } : browser}
            className={tabNamesClassName}
            inline={inline}
            onChange={handleOnChange}
            scroll={scroll}
            selectedID={tabID}
            styler={tabNamesStyler}
            theme={theme as never}
            titles={headerTitles}
        />
    );

    return (
        <div ref={forwardedRef} className={cn(styles.tabs, className)}>
            {background ? cloneElement(background as ReactElement, { children: List }) : List}
            <div>
                {childrenFiltered.map(child =>
                    cloneElement(child, {
                        hidden: child.props.id !== tabID,
                    })
                )}
            </div>
        </div>
    );
};
